/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import Router from 'next/router';
import APM from '../APM';
import getInitializationMonitor from '../monitors/InitializationPerformanceMonitor';
import getMonitor from '../monitors/PagePerformanceMonitor';

/**
 * The `PagePerformanceAutoMonitor` API is only ever to be initialized once
 * per session. The `initialized` flag ensures any and all subsequent calls
 * are bypassed once initialized.
 */
let initialized = false;

/**
 * Provides a reference to the currently active route / page which is used to
 * verify route changes; i.e. ensure a route change event is unique.
 */
let currentPage: string;

/**
 * Provides a mechanism which allows for automatically monitoring page performance.
 * Note, clients must also ensure to invoke `PagePerformanceMonitor.stop();` in order
 * to complete measuring page performance. The `autoMonitor` utility simply serves as
 * a mean of automatically starting page performance monitoring, but does not have a
 * means of automatically stopping page performance monitoring as the time at which
 * monitoring should be completed is arbitrarily based on the components within the
 * given page as well as the async services associated with the page components.
 *
 * Typically, applications are to invoke the `autoMonitorPagePerformance` function
 * within the application's `_app.tsx` module after the environment.json configuration
 * has been loaded.
 *
 * ```typescript
 *   // ion/ion-app/src/pages/_app.tsx
 *   import autoMonitorPagePerformance from '@ion/applications/src/apm';
 *   ...
 *
 *   useEffect(() => {
 *     if (isMounted && !initialized && !isInitializing) {
 *       ...
 *       loadConfig(config => {
 *         if (config.pagePerformanceEnabled) {
 *           // begin automatic page performance monitoring ...
 *           autoMonitorPagePerformance();
 *         }
 *         ...
 *       });
 *     }
 *   }, [isMounted, initialized, isInitializing]);
 * ```
 *
 * @param autoStopInitialization When `true` (default), the Initialization Performance
 *        Monitor will automatically be stopped (all measurements will be completed)
 *        upon the first route change as, it is at this point that the first page
 *        will begin being monitored and the Initialization process has been completed.
 */
export const autoMonitorPagePerformance = (autoStopInitialization = true) => {
  if (APM.isEnabled) {
    if (!initialized) {
      initialized = true;
      Router.events.on('routeChangeStart', (route: string) => {
        const monitor = getMonitor();
        if (route !== currentPage) {
          if (autoStopInitialization) {
            autoCompleteInitializationMonitor();
          }
          if (monitor.isRunning()) {
            monitor.stop(true);
          }
          monitor.start(route);
          currentPage = route;
        }
      });
    }
  } else {
    initialized = false;
  }
};

/**
 * Provides a convenience function which is invoked when the `autoStopInitialization`
 * argument of the `autoMonitorPagePerformance` function is set to `true`, at which
 * point if the `InitializationPerformanceMonitor` is currently running, it will be
 * stopped (all measurements will be completed).
 */
const autoCompleteInitializationMonitor = () => {
  const initializationMonitor = getInitializationMonitor();
  if (initializationMonitor?.isRunning()) {
    initializationMonitor.stop();
  }
};
export default autoMonitorPagePerformance;
